/**
 * Home Card (In-App Message) Entity Schemas
 *
 * The v1 in-app message rendered as a home-screen card. A home card is a
 * `ContentRecord` with `presentationStyle = HOME_CARD`, delivered as a live
 * current-state DB read (no Stream activity, no fan-out). See
 * docs/plans/2026-05-25-001-feat-app-home-messages-pure-db-plan.md.
 */

import { z } from 'zod'

/**
 * Home-card *kind* discriminator. `message` is the classic in-app message that
 * taps through to the built-in announcement detail modal; `link` is a card whose
 * whole purpose is to open a URL in the in-app web view on tap (no detail page).
 * Explicit discriminator (not inferred from `linkUrl` presence) — clearer for the
 * web chooser, analytics, and future kinds. See plan 2026-07-09-004 KTD2.
 */
export const CardKindSchema = z.enum(['message', 'link'])
export type CardKind = z.infer<typeof CardKindSchema>

/**
 * Max length of a link card's short description (the ~2-line card copy). Used by
 * the web authoring char counter to cap input; sized to match the mobile card's
 * 2-line render budget (mobile clamps visually via `numberOfLines`, so it does
 * not import this value). See plan KTD/U4/U5.
 */
export const LINK_CARD_DESCRIPTION_MAX = 160

/**
 * True when `value` parses as a URL whose scheme is `http:`/`https:`. `z.url()`
 * alone accepts `javascript:`/`mailto:`/`data:` (WHATWG parser semantics), so a
 * link card's URL MUST pass this explicit scheme gate — enforced both here in the
 * wire contract and server-side at the content-create request schema (KTD3 #3).
 */
export function isHttpUrl(value: string): boolean {
  try {
    const protocol = new URL(value).protocol.toLowerCase()
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Shared zod `superRefine` for the two link-card fields. When `cardKind === 'link'`
 * the `linkUrl` is required and must be an `http(s)` URL; for a message card the
 * `linkUrl` is ignored. Applied at both the read/view contract and the backend
 * content-create request schema so a direct `POST` cannot bypass the check.
 */
export function refineLinkCardFields(
  fields: { cardKind?: CardKind | null; linkUrl?: string | null },
  ctx: z.RefinementCtx
): void {
  const kind = fields.cardKind ?? 'message'
  if (kind !== 'link') return
  const url = fields.linkUrl
  if (!url || url.trim().length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['linkUrl'],
      message: 'linkUrl is required for a link card'
    })
    return
  }
  if (!isHttpUrl(url)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['linkUrl'],
      message: 'linkUrl must be an http:// or https:// web address'
    })
  }
}

/**
 * The view of a home card returned to a reader by GET /v1/home. Keyed on the
 * underlying ContentRecord `publicId` (the stable dismissal key). `dismissed`
 * reflects this reader's own interaction state.
 */
export const HomeCardViewSchema = z
  .object({
    publicId: z.string(),
    title: z.string().nullable(),
    body: z.string().nullable(),
    /** Cover/hero image URL when the card has one. */
    imageUrl: z.string().nullable(),
    /**
     * Card kind. Defaults to `message` so pre-`cardKind` cached payloads and rows
     * read back as classic message cards (old-client backward-compat).
     */
    cardKind: CardKindSchema.default('message'),
    /** Target URL for a `link` card; null for a message card. */
    linkUrl: z.string().nullable().default(null),
    pinned: z.boolean(),
    dismissible: z.boolean(),
    /** DB-canonical notification mode (NONE|NORMAL|ALL|EMERGENCY) — drives priority styling. */
    notificationMode: z.enum(['NONE', 'NORMAL', 'ALL', 'EMERGENCY']),
    publishedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    /** This reader's interaction state. dismissed is true once dismissedAt is set. */
    dismissed: z.boolean()
    // NOTE: per-user `seenAt` is intentionally NOT surfaced in v1. "Seen" is
    // tracked client-side via PostHog (impression), not server-side — the
    // UserItemInteraction.seenAt column is reserved for a future server-seen
    // surface (and parent-action-items alignment). Add it back here with a real
    // write path when that lands. See review P2-10.
  })
  .superRefine((data, ctx) => refineLinkCardFields(data, ctx))

export type HomeCardView = z.infer<typeof HomeCardViewSchema>
